import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {
    Button,
    Box,
    Image,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import CustomDropdown from '../../../components/custom-dropwdown'
import {getUrl} from '../../../../../utils/cloudinary-utils'
import Link from '../../../../link'
import usePdTagManager from '../../../../../hooks/use-pd-tag-manager'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

const HeroButton = ({
    text,
    url,
    color,
    bgColor,
    buttonTextColor,
    buttonTextColorDesktop,
    buttonBgColor,
    ctaSizeMobile,
    ctaSizeDesktop,
    ctaTypeMobile,
    ctaTypeDesktop,
    fontFamily,
    onButtonClick,
    name,
    typeId,
    subtitle,
    titlePosition,
    imageUrl,
    videoUrl,
    dropdownLinks = []
}) => {
    const pdTagManager = usePdTagManager()
    const appOrigin = useAppOrigin()
    const isDropdown = ctaTypeMobile === 'dropdown' || ctaTypeDesktop === 'dropdown'
    const isTextWithArrow = ctaTypeMobile === 'textWithArrow' || ctaTypeDesktop === 'textWithArrow'

    const variant = {
        base: ctaTypeMobile === 'button' ? 'heroButton' : 'linkUnderline',
        md: ctaTypeDesktop === 'button' ? 'heroButton' : 'linkUnderline'
    }

    const styles = useStyleConfig('FormStyles')

    // Normalize and helpers
    const normalizedBgColor = buttonBgColor?.trim().toLowerCase()

    const isWhite = (val) => val === 'white' || val === '#fff' || val === '#ffffff'
    const isBlack = (val) => val === 'black' || val === '#000' || val === '#000000'

    // Normalize light/dark support
    const isLight = color === 'light' || isWhite(color)
    const isDark = color === 'dark' || isBlack(color)

    const hasCustomBgColor =
        !!buttonBgColor && !isWhite(normalizedBgColor) && !isBlack(normalizedBgColor)

    const getColorValue = (value, fallback) => {
        if (!value || isLight || isDark) return fallback
        return value
    }

    const background = {
        base:
            ctaTypeMobile === 'button'
                ? hasCustomBgColor
                    ? buttonBgColor
                    : isDark
                    ? 'black'
                    : isLight
                    ? 'white'
                    : getColorValue(bgColor, 'white')
                : 'transparent',
        md:
            ctaTypeDesktop === 'button'
                ? hasCustomBgColor
                    ? buttonBgColor
                    : isDark
                    ? 'black'
                    : isLight
                    ? 'white'
                    : getColorValue(bgColor, 'white')
                : 'transparent'
    }

    const hoverBg = {
        base:
            ctaTypeMobile === 'button'
                ? hasCustomBgColor
                    ? buttonBgColor
                    : isLight
                    ? 'white'
                    : isDark
                    ? 'black'
                    : getColorValue(bgColor, 'black')
                : 'unset',
        md:
            ctaTypeDesktop === 'button'
                ? hasCustomBgColor
                    ? buttonBgColor
                    : isLight
                    ? 'white'
                    : isDark
                    ? 'black'
                    : getColorValue(bgColor, 'black')
                : 'unset'
    }

    const isWhiteBg = (colorValue) => {
        const color = colorValue?.trim().toLowerCase()
        return color === 'white' || color === '#fff' || color === '#ffffff'
    }

    const getHoverMix = (bg) =>
        isWhiteBg(bg)
            ? 'color-mix(in srgb, white 90%, black 10%)'
            : `color-mix(in srgb, ${bg} 88%, white 12%)`

    const size = {
        base: ctaTypeMobile === 'button' ? ctaSizeMobile : 'default',
        md: ctaTypeDesktop === 'button' ? ctaSizeDesktop : 'default'
    }

    const textColor = {
        base: buttonTextColor,
        lg: buttonTextColorDesktop
    }
    const route = url?.split('.com')?.[1]
    const isExternalDomain = url?.indexOf('http') !== -1
    const linkProps = isExternalDomain
        ? {to: url, as: Link, isExternal: true}
        : {as: RouteLink, to: route || url}
    if (isTextWithArrow) {
        const isWhiteText =
            buttonTextColor === 'white' ||
            (!buttonTextColor && (color === 'light' || color === 'white'))

        return (
            <Box
                {...linkProps}
                display="inline-flex"
                alignItems="center"
                color={textColor.base}
                fontFamily={fontFamily}
                textDecoration="none"
                zIndex="1"
            >
                {text}
                <Image
                    alt="Arrow Icon"
                    src={getUrl(
                        isWhiteText
                            ? 'UIImages/images/arrow-right-white.svg'
                            : 'UIImages/images/arrow-right.svg'
                    )}
                    {...styles.selectIcon}
                    width="18px"
                    height="10px"
                    ml="15px"
                />
            </Box>
        )
    }

    if (isDropdown && dropdownLinks.length > 0) {
        return (
            <CustomDropdown
                text={text}
                links={dropdownLinks}
                fontFamily={fontFamily}
                buttonTextColor={buttonTextColor}
                buttonTextColorDesktop={buttonTextColorDesktop}
                buttonBgColor={buttonBgColor}
                typeId={typeId}
                name={name}
                imageUrl={imageUrl}
                videoUrl={videoUrl}
            />
        )
    }

    return (
        <Button
            {...linkProps}
            onClick={() => {
                if (onButtonClick) {
                    onButtonClick()
                } else {
                    pdTagManager.selectComponent({
                        componentId: typeId,
                        componentName: name,
                        titleName: text,
                        subText: subtitle,
                        linkUrl: url?.indexOf('http') !== -1 ? url : `${appOrigin}${url}`,
                        titlePosition: titlePosition,
                        videoUrl: videoUrl,
                        imageUrl: imageUrl
                    })
                }
            }}
            variant={variant}
            background={background}
            color={textColor}
            fontFamily={fontFamily}
            position="relative"
            aria-label={text}
            size={size}
            width="auto !important"
            minWidth="auto !important"
            zIndex="1"
            sx={{
                '--btn-hover-mix': {
                    base: getHoverMix(hoverBg.base),
                    md: getHoverMix(hoverBg.md)
                },
                transition: 'background-color .15s ease',
                _hover: {
                    backgroundColor: 'var(--btn-hover-mix)',
                    textDecoration: 'none'
                }
            }}
        >
            {text}
        </Button>
    )
}

HeroButton.propTypes = {
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    color: PropTypes.string,
    bgColor: PropTypes.string,
    buttonTextColor: PropTypes.string,
    buttonTextColorDesktop: PropTypes.string,
    buttonBgColor: PropTypes.string,
    ctaSizeMobile: PropTypes.string,
    ctaSizeDesktop: PropTypes.string,
    ctaTypeMobile: PropTypes.string,
    ctaTypeDesktop: PropTypes.string,
    fontFamily: PropTypes.string,
    dropdownLinks: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            url: PropTypes.string
        })
    ),
    onButtonClick: PropTypes.func,
    typeId: PropTypes.string,
    subtitle: PropTypes.any,
    titlePosition: PropTypes.any,
    imageUrl: PropTypes.any,
    videoUrl: PropTypes.any,
    name: PropTypes.any
}

export default HeroButton
